import { groupRefOf, normalizeGroupRef, DEFAULT_NAMESPACE } from './groupRef';
import type { WoboGroupLike } from './wobo';

/**
 * Every Group transitively below `rootRef`, found by walking `spec.parent`
 * bottom-up (never `spec.children`). Cycle-safe; excludes `rootRef`. A node
 * matched by `stopAt` and its whole subtree are excluded.
 */
export function collectDescendants<G extends WoboGroupLike>(
  rootRef: string,
  groups: Iterable<G>,
  options?: { stopAt?: (group: G) => boolean },
): G[] {
  const childrenByParent = new Map<string, G[]>();
  for (const group of groups) {
    const parentRef = normalizeGroupRef(
      group.spec?.parent,
      group.metadata.namespace?.trim() || DEFAULT_NAMESPACE,
    );
    if (!parentRef) {
      continue;
    }
    const siblings = childrenByParent.get(parentRef);
    if (siblings) {
      siblings.push(group);
    } else {
      childrenByParent.set(parentRef, [group]);
    }
  }

  const result: G[] = [];
  const visited = new Set<string>([rootRef]);
  const queue = [rootRef];
  while (queue.length) {
    const current = queue.shift()!;
    for (const child of childrenByParent.get(current) ?? []) {
      const childRef = groupRefOf(child);
      if (visited.has(childRef)) {
        continue;
      }
      visited.add(childRef);
      if (options?.stopAt?.(child)) {
        continue;
      }
      result.push(child);
      queue.push(childRef);
    }
  }
  return result;
}
